/**
 * Notification Container Component
 * Displays toast-style notifications in a fixed position.
 * WCAG 2.1 AA compliant with ARIA live regions for screen reader announcements.
 * @packageDocumentation
 */

import { useTranslation } from 'react-i18next';
import { X, Info, XCircle, CheckCircle, AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useNotificationStore, type NotificationType } from '@/stores';

/** Icon mapping for each notification type. */
const icons: Record<NotificationType, typeof CheckCircle> = {
  info: Info,
  error: XCircle,
  success: CheckCircle,
  warning: AlertTriangle,
};

/** Left accent bar color per notification type. */
const accentStyles: Record<NotificationType, string> = {
  info: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-destructive',
};

/** Icon circle background and border per notification type (light + dark). */
const iconCircleStyles: Record<NotificationType, string> = {
  info: 'bg-primary/10 border border-primary/25 text-primary',
  success: 'bg-success/10 border border-success/25 text-success',
  warning: 'bg-warning/10 border border-warning/25 text-warning',
  error: 'bg-destructive/10 border border-destructive/25 text-destructive',
};

const ariaLiveMap: Record<NotificationType, 'assertive' | 'polite'> = {
  info: 'polite',
  success: 'polite',
  error: 'assertive',
  warning: 'assertive',
};

const roleMap: Record<NotificationType, 'alert' | 'status'> = {
  error: 'alert',
  info: 'status',
  warning: 'alert',
  success: 'status',
};

/**
 * Container component for displaying toast notifications.
 * Uses ARIA live regions for screen reader announcements:
 * - Error/Warning: assertive (interrupts current speech)
 * - Success/Info: polite (waits for pause in speech)
 *
 * @returns { JSX.Element | null } The notification container or null if empty.
 */
export const NotificationContainer = () => {
  const { t } = useTranslation();
  const { notifications, removeNotification } = useNotificationStore();

  return (
    <section className="fixed right-4 top-4 z-50 w-[380px] space-y-2" aria-label={t('aria.notifications')}>
      {notifications.map((notification) => {
        const Icon = icons[notification.type];

        return (
          <div
            key={notification.id}
            role={roleMap[notification.type]}
            aria-live={ariaLiveMap[notification.type]}
            aria-atomic="true"
            className="relative overflow-hidden rounded-xl border border-border bg-card shadow-lg motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:zoom-in-[0.97] motion-safe:duration-[450ms]"
          >
            {/* Left accent bar */}
            <div
              className={cn('absolute bottom-0 left-0 top-0 w-1 rounded-l-xl', accentStyles[notification.type])}
              aria-hidden="true"
            />

            {/* Content */}
            <div className="flex items-center gap-3.5 py-3.5 pl-5 pr-4">
              {/* Icon circle */}
              <div
                className={cn(
                  'flex size-9 flex-shrink-0 items-center justify-center rounded-full',
                  iconCircleStyles[notification.type],
                )}
                aria-hidden="true"
              >
                <Icon className="size-4" strokeWidth={2.5} />
              </div>

              {/* Body */}
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold leading-snug text-foreground">{notification.title}</p>
                {notification.message && (
                  <p className="mt-0.5 break-words text-[13px] leading-relaxed text-muted-foreground">
                    {notification.message}
                  </p>
                )}
              </div>

              {/* Close button */}
              <button
                type="button"
                onClick={() => removeNotification(notification.id)}
                className="flex size-7 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('aria.dismissNotification', { title: notification.title })}
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
};
